import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import MonthlyAttendanceEntry from './MonthlyAttendanceEntry';
import EnhancedTeacherAttendanceView from './EnhancedTeacherAttendanceView';
import { getCurrentAcademicSession, getDateType, getDateColor, getDateLabel } from '../utils/academicSession';

function AttendanceCalendar({ userClass, userRole, students, currentDate: propCurrentDate, onDateChange, userId, onDateClick }) {
  const [currentDate, setCurrentDate] = useState(propCurrentDate || new Date());
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [workingDays, setWorkingDays] = useState([]);
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [showMonthlyEntry, setShowMonthlyEntry] = useState(false);
  const [attendanceVisibility, setAttendanceVisibility] = useState(true);
  const [teacherAttendance, setTeacherAttendance] = useState([]);
  const [showTeacherAttendanceView, setShowTeacherAttendanceView] = useState(false);

  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  
  useEffect(() => {
    if (propCurrentDate) {
      setCurrentDate(propCurrentDate);
    }
  }, [propCurrentDate]);

  useEffect(() => {
    fetchAttendanceRecords();
    fetchWorkingDays();
    fetchEvents();
    if (userRole === 'student') {
      fetchAttendanceVisibility();
    }
    if (userRole === 'teacher') {
      fetchTeacherAttendance();
    }
  }, [currentDate, userClass]);
  
  const fetchAttendanceRecords = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/attendance-records/${userClass}`);
      const allRecords = Array.isArray(response.data) ? response.data : [];
      
      // Filter records to only include the current month and year
      let filteredRecords = allRecords.filter(record => {
        const recordDate = new Date(record.date);
        return recordDate.getMonth() === month - 1 && recordDate.getFullYear() === year;
      });
      
      // If this is a student view, filter by student ID
      if (userRole === 'student' && userId) {
        filteredRecords = filteredRecords.filter(record => record.studentId === userId);
      }
      
      setAttendanceRecords(filteredRecords);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setAttendanceRecords([]);
    }
  };
  
  const fetchWorkingDays = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/working-days/${userClass}/${year}/${month}`);
      setWorkingDays(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching working days:', error);
      setWorkingDays([]);
    }
  };
  
  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/calendar-events/${userClass}`);
      setEvents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    }
  };

  const fetchAttendanceVisibility = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/attendance-visibility/${userClass}`);
      setAttendanceVisibility(response.data.showDailyStatus);
    } catch (error) {
      console.error('Error fetching attendance visibility:', error);
      setAttendanceVisibility(true);
    }
  };

  const fetchTeacherAttendance = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/teacher-attendance/${userId}`, {
        params: { 
          month: currentDate.getMonth() + 1, 
          year: currentDate.getFullYear() 
        }
      });
      setTeacherAttendance(response.data || []);
    } catch (error) {
      console.error('Error fetching teacher attendance:', error);
      setTeacherAttendance([]);
    }
  };

  const getTeacherAttendanceStats = () => {
    const presentDays = teacherAttendance.filter(record => record.status === 'present').length;
    const absentDays = teacherAttendance.filter(record => record.status === 'absent').length;
    const lateDays = teacherAttendance.filter(record => record.status === 'late').length;
    const firstHalfDays = teacherAttendance.filter(record => record.status === 'first_half').length;
    const secondHalfDays = teacherAttendance.filter(record => record.status === 'second_half').length;
    const totalMarked = presentDays + absentDays + lateDays + firstHalfDays + secondHalfDays;
    const percentage = totalMarked > 0 ? ((presentDays + lateDays + (firstHalfDays * 0.5) + (secondHalfDays * 0.5)) / totalMarked * 100).toFixed(1) : 0;

    return {
      presentDays,
      absentDays,
      lateDays,
      firstHalfDays,
      secondHalfDays,
      totalMarked,
      percentage
    };
  };

  const getTeacherAttendanceForDate = (date) => {
    return teacherAttendance.find(record => record.date === date);
  };
  
  const markAttendance = async (studentId, date, status) => {
    try {
      await axios.post(`${API_BASE_URL}/api/attendance-records`, {
        studentId,
        class: userClass,
        date,
        status,
        markedBy: 'Teacher'
      });
      fetchAttendanceRecords();
      alert(`Attendance marked as ${status}`);
    } catch (error) {
      console.error('Error marking attendance:', error);
    }
  };
  
  const getAttendanceForDate = (studentId, date) => {
    const recordsArray = Array.isArray(attendanceRecords) ? attendanceRecords : [];
    return recordsArray.find(record => record.studentId === studentId && record.date === date);
  };
  
  const getDayType = (day) => {
    const date = new Date(year, month - 1, day);
    const eventsArray = Array.isArray(events) ? events : [];
    const workingDaysArray = Array.isArray(workingDays) ? workingDays : [];
    return getDateType(date, workingDaysArray, eventsArray);
  };
  
  const isAttendanceDay = (day) => {
    const dayType = getDayType(day);
    return (dayType === 'working' || dayType === 'celebration') && dayType !== 'summer-vacation';
  };
  
  const getDayColor = (day) => {
    const type = getDayType(day);
    return getDateColor(type);
  };
  
  const isToday = (day) => {
    const today = new Date();
    return today.getDate() === day && 
           today.getMonth() === month - 1 && 
           today.getFullYear() === year;
  };
  
  const renderCalendarDays = () => {
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} style={{ padding: '8px' }}></div>);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayType = getDayType(day);
      const canMarkAttendance = isAttendanceDay(day);
      
      days.push(
        <div
          key={day}
          onClick={() => {
            if (canMarkAttendance && userRole === 'teacher') {
              if (onDateClick) {
                onDateClick(dateStr);
              } else {
                setSelectedDate(dateStr);
              }
            }
          }}
          style={{
            padding: '8px',
            textAlign: 'center',
            cursor: canMarkAttendance && userRole === 'teacher' ? 'pointer' : 'default',
            borderRadius: '4px',
            background: getDayColor(day),
            color: 'white',
            fontSize: '14px',
            fontWeight: 'bold',
            border: selectedDate === dateStr ? '3px solid #000' : 
                   isToday(day) ? '2px solid #fff' : 'none',
            boxShadow: isToday(day) ? '0 0 8px rgba(255,255,255,0.8), inset 0 0 10px rgba(255,255,255,0.3)' : 'none',
            transform: isToday(day) ? 'scale(1.05)' : 'scale(1)',
            transition: 'all 0.2s ease',
            position: 'relative'
          }}
        >
          {day}
          {userRole === 'student' && userId && attendanceVisibility && (() => {
            const attendance = getAttendanceForDate(userId, dateStr);
            if (attendance) {
              return (
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  right: '2px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  color: attendance.status === 'present' ? '#4caf50' : '#f44336',
                  background: 'white',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {attendance.status === 'present' ? 'P' : 'A'}
                </div>
              );
            }
            return null;
          })()}
        </div>
      );
    }
    
    return days;
  };
  
  const prevMonth = () => {
    const newDate = new Date(year, month - 2, 1);
    setCurrentDate(newDate);
    if (onDateChange) onDateChange(newDate);
  };
  const nextMonth = () => {
    const newDate = new Date(year, month, 1);
    setCurrentDate(newDate);
    if (onDateChange) onDateChange(newDate);
  };
  
  return (
    <div style={{ background: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: 0, color: '#1e40af' }}>📅 Attendance Calendar - {monthNames[month - 1]} {year}</h3>
          <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            Academic Session: {getCurrentAcademicSession()} | {userRole === 'student' ? `Showing ${monthNames[month - 1]} ${year} data` : 'Session started in April'}
          </p>
        </div>
        <div>
          <button onClick={prevMonth} style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer', marginRight: '5px' }}>‹</button>
          <button onClick={nextMonth} style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '4px', cursor: 'pointer' }}>›</button>
        </div>
      </div>

      {/* Teacher's Personal Attendance Summary */}
      {userRole === 'teacher' && userId && (
        <div style={{ 
          background: 'linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%)', 
          borderRadius: '12px', 
          padding: '20px', 
          marginBottom: '20px',
          border: '2px solid #0288d1'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h4 style={{ margin: 0, color: '#01579b', fontSize: '18px', fontWeight: 'bold' }}>
              👨🏫 My Attendance - {monthNames[month - 1]} {year}
            </h4>
            <button 
              onClick={() => setShowTeacherAttendanceView(true)}
              style={{ 
                background: 'linear-gradient(135deg, #0288d1 0%, #0277bd 100%)', 
                color: 'white', 
                border: 'none', 
                padding: '8px 16px', 
                borderRadius: '8px', 
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(2, 136, 209, 0.3)'
              }}
            >
              📊 View Details
            </button>
          </div>
          
          {(() => {
            const stats = getTeacherAttendanceStats();
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
                <div style={{ 
                  background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)', 
                  color: 'white', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>{stats.presentDays}</div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Present Days</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', 
                  color: 'white', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>{stats.lateDays}</div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Late Days</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #f44336 0%, #e53935 100%)', 
                  color: 'white', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>{stats.absentDays}</div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Absent Days</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', 
                  color: 'white', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(156, 39, 176, 0.3)'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>{stats.firstHalfDays}</div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>First Half</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #673ab7 0%, #512da8 100%)', 
                  color: 'white', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(103, 58, 183, 0.3)'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>{stats.secondHalfDays}</div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Second Half</div>
                </div>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', 
                  color: 'white', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>{stats.percentage}%</div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>Attendance Rate</div>
                </div>
              </div>
            );
          })()}
          
          {/* Quick Daily View */}
          <div style={{ marginTop: '15px' }}>
            <h5 style={{ margin: '0 0 12px 0', color: '#01579b', fontSize: '16px', fontWeight: 'bold' }}>📅 This Month's Daily Record:</h5>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '4px',
              padding: '12px',
              background: 'rgba(255,255,255,0.8)',
              borderRadius: '10px',
              border: '1px solid rgba(1,87,155,0.2)'
            }}>
              {/* Day headers */}
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((dayName, index) => (
                <div key={index} style={{
                  textAlign: 'center',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: '#666',
                  padding: '4px'
                }}>
                  {dayName}
                </div>
              ))}
              
              {(() => {
                const firstDay = new Date(year, month - 1, 1).getDay();
                const daysInMonth = new Date(year, month, 0).getDate();
                const days = [];
                
                // Empty cells for days before month starts
                for (let i = 0; i < firstDay; i++) {
                  days.push(<div key={`empty-${i}`} style={{ padding: '4px' }}></div>);
                }
                
                // Days of the month
                for (let day = 1; day <= daysInMonth; day++) {
                  const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const attendance = getTeacherAttendanceForDate(dateStr);
                  const dayType = getDayType(day);
                  const isWorkingDay = dayType === 'working' || dayType === 'celebration';
                  
                  days.push(
                    <div
                      key={day}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        background: !isWorkingDay ? '#e0e0e0' :
                                   attendance?.status === 'present' ? '#4caf50' :
                                   attendance?.status === 'late' ? '#ff9800' :
                                   attendance?.status === 'first_half' ? '#9c27b0' :
                                   attendance?.status === 'second_half' ? '#673ab7' :
                                   attendance?.status === 'absent' ? '#f44336' : '#f5f5f5',
                        color: !isWorkingDay ? '#666' : 
                              attendance ? 'white' : '#666',
                        border: isToday(day) ? '2px solid #01579b' : '1px solid rgba(0,0,0,0.1)',
                        cursor: attendance ? 'pointer' : 'default',
                        margin: '0 auto'
                      }}
                      title={!isWorkingDay ? `${day} - Holiday/Weekend` :
                            attendance ? `${day} - ${attendance.status.toUpperCase()}${attendance.remarks ? ` (${attendance.remarks})` : ''}` :
                            `${day} - Not marked`}
                    >
                      {day}
                    </div>
                  );
                }
                
                return days;
              })()}

            </div>
          </div>
        </div>
      )}
      
      <div style={{ fontSize: '12px', marginBottom: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ background: '#3b82f6', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>Blue: Working Days</span>
          <span style={{ background: '#ff6b35', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>Orange: Summer Vacation (May 16 - Jun 30)</span>
          <span style={{ background: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>Red: Sundays/Holidays</span>
          <span style={{ background: '#fbbf24', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>Yellow: Celebrations</span>
        </div>
        {userRole === 'teacher' && !(month === 5 || month === 6) && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              onClick={() => setShowMonthlyEntry(true)}
              style={{ 
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                color: 'white', 
                border: 'none', 
                padding: '6px 12px', 
                borderRadius: '6px', 
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
              }}
            >
              📅 Monthly Entry
            </button>

          </div>
        )}
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '8px' }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={{ textAlign: 'center', fontSize: '12px', fontWeight: '600', padding: '8px', color: '#666' }}>{day}</div>
        ))}
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '20px' }}>
        {renderCalendarDays()}
      </div>
      
      {/* Summer Vacation Notice */}
      {(month === 5 || month === 6) && (
        <div style={{ 
          background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)', 
          color: 'white', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '15px',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)'
        }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>🏖️ Summer Vacation Period</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>
            {month === 5 ? 'May 16-31: Summer vacation in progress' : 'June 1-30: Summer vacation continues'}
          </div>
        </div>
      )}
      
      {userRole === 'teacher' && selectedDate && !getDayType(parseInt(selectedDate.split('-')[2])).includes('summer-vacation') && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px' }}>
          <h4 style={{ color: '#1e40af' }}>Mark Attendance for {selectedDate}</h4>
          <div style={{ maxHeight: '200px', overflow: 'auto' }}>
            {students.map(student => {
              const attendance = getAttendanceForDate(student._id, selectedDate);
              return (
                <div key={student._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', background: '#f8f9fa', borderRadius: '4px', marginBottom: '5px' }}>
                  <span>{student.name}</span>
                  <div>
                    <span style={{ marginRight: '10px', fontSize: '12px', color: attendance ? (attendance.status === 'present' ? '#10b981' : '#ef4444') : '#666' }}>
                      {attendance ? (attendance.status === 'present' ? '✓ Present' : '✗ Absent') : 'Not Marked'}
                    </span>
                    <button onClick={() => markAttendance(student._id, selectedDate, 'present')} style={{ background: '#10b981', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', marginRight: '5px' }}>P</button>
                    <button onClick={() => markAttendance(student._id, selectedDate, 'absent')} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>A</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {userRole === 'student' && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px' }}>
          <h4 style={{ color: '#1e40af' }}>My Attendance Summary - {monthNames[month - 1]} {year}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
            <div style={{ background: '#10b981', color: 'white', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{Array.isArray(attendanceRecords) ? attendanceRecords.filter(r => r.status === 'present').length : 0}</div>
              <div style={{ fontSize: '12px' }}>Present</div>
            </div>
            <div style={{ background: '#ef4444', color: 'white', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{Array.isArray(attendanceRecords) ? attendanceRecords.filter(r => r.status === 'absent').length : 0}</div>
              <div style={{ fontSize: '12px' }}>Absent</div>
            </div>
            <div style={{ background: '#3b82f6', color: 'white', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{Array.isArray(workingDays) ? workingDays.length : 0}</div>
              <div style={{ fontSize: '12px' }}>Working Days</div>
            </div>
            {(month === 5 || month === 6) && (
              <div style={{ background: '#ff6b35', color: 'white', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                  {Array.isArray(workingDays) ? workingDays.filter(dateStr => {
                    const date = new Date(dateStr);
                    const m = date.getMonth() + 1;
                    const d = date.getDate();
                    return (m === 5 && d >= 16) || m === 6;
                  }).length : 0}
                </div>
                <div style={{ fontSize: '12px' }}>Summer Vacation</div>
              </div>
            )}
          </div>
          {!attendanceVisibility && (
            <div style={{ 
              marginTop: '10px',
              padding: '8px 12px', 
              background: '#fff3cd', 
              borderRadius: '6px', 
              textAlign: 'center',
              color: '#856404',
              fontSize: '12px',
              border: '1px solid #ffeaa7'
            }}>
              👁️ Daily P/A indicators are currently hidden on calendar days
            </div>
          )}
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            background: '#e3f2fd', 
            borderRadius: '6px',
            fontSize: '12px',
            color: '#1565c0'
          }}>
            📚 Academic Session {getCurrentAcademicSession()} started in April. Summer vacation: May 16 - June 30.
          </div>
        </div>
      )}
      
      {showMonthlyEntry && (
        <MonthlyAttendanceEntry 
          userClass={userClass}
          students={students}
          onClose={() => {
            setShowMonthlyEntry(false);
            fetchAttendanceRecords(); // Refresh attendance data
          }}
        />
      )}

      {showTeacherAttendanceView && (
        <EnhancedTeacherAttendanceView 
          teacherId={userId}
          teacherName={localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).name : 'Teacher'}
          userRole={userRole}
          onClose={() => setShowTeacherAttendanceView(false)}
        />
      )}
      
      {/* Events and Holidays Details Section */}
      {events.length > 0 && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px', marginTop: '15px' }}>
          <h4 style={{ color: '#1e40af', marginBottom: '10px' }}>📅 Events & Holidays - {monthNames[month - 1]} {year}</h4>
          <div style={{ maxHeight: '200px', overflow: 'auto' }}>
            {events
              .filter(event => {
                const eventDate = new Date(event.date);
                return eventDate.getMonth() === month - 1 && eventDate.getFullYear() === year;
              })
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .map(event => {
                const eventDate = new Date(event.date);
                const dayName = eventDate.toLocaleDateString('en-US', { weekday: 'long' });
                const formattedDate = eventDate.toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric' 
                });
                
                const getEventIcon = (type) => {
                  switch(type) {
                    case 'holiday': return '🏖️';
                    case 'celebration': return '🎉';
                    case 'vacation': return '🌴';
                    case 'exam': return '📝';
                    case 'pt1': case 'pt2': return '📊';
                    default: return '📅';
                  }
                };
                
                const getEventColor = (type) => {
                  switch(type) {
                    case 'holiday': return '#ef4444';
                    case 'celebration': return '#fbbf24';
                    case 'vacation': return '#ff6b35';
                    case 'exam': return '#8b5cf6';
                    case 'pt1': case 'pt2': return '#06b6d4';
                    default: return '#3b82f6';
                  }
                };
                
                return (
                  <div key={event._id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '10px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    borderLeft: `4px solid ${getEventColor(event.type)}`
                  }}>
                    <div style={{ 
                      fontSize: '24px', 
                      marginRight: '12px',
                      minWidth: '32px',
                      textAlign: 'center'
                    }}>
                      {getEventIcon(event.type)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: 'bold', 
                        color: '#1e40af',
                        fontSize: '14px',
                        marginBottom: '2px'
                      }}>
                        {event.title}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#666',
                        marginBottom: '2px'
                      }}>
                        {dayName}, {formattedDate} • {event.type.charAt(0).toUpperCase() + event.type.slice(1)}
                      </div>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#888',
                        fontStyle: 'italic'
                      }}>
                        {event.isAdminEvent ? 'School-wide event' : `Class ${event.class} event`} • Added by {event.createdBy}
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </div>
        </div>
      )}

    </div>
  );
}

export default AttendanceCalendar;